/**
 * Prefetch utilities for improving user engagement and perceived performance
 * Uses hover states and background tasks to prefetch data before user actions
 */

// Cache for prefetched components
const componentCache = new Map();
const prefetchPromises = new Map();

/**
 * Prefetch a component module
 */
export const prefetchComponent = (importFn, componentName) => {
  // If already cached or prefetching, return existing promise
  if (componentCache.has(componentName)) {
    return Promise.resolve(componentCache.get(componentName));
  }
  
  if (prefetchPromises.has(componentName)) {
    return prefetchPromises.get(componentName);
  }

  // Start prefetching
  const promise = importFn().then((module) => {
    componentCache.set(componentName, module);
    prefetchPromises.delete(componentName);
    return module;
  });

  prefetchPromises.set(componentName, promise);
  return promise;
};

/**
 * Prefetch note data when hovering over a note snippet
 */
export const prefetchNoteData = async (noteId, notes) => {
  // If note already exists in context, no need to prefetch
  if (notes && notes[noteId]) {
    return Promise.resolve(notes[noteId]);
  }

  // In the future, could prefetch from API here
  // For now, this is a placeholder for when we add API prefetching
  return Promise.resolve(null);
};

/**
 * Prefetch panel components on tab hover
 */
export const prefetchPanelComponent = (componentName) => {
  const componentMap = {
    notes: () => import('../views/panels/NotesPanel'),
    info: () => import('../views/panels/EntityInfoPanel'),
    notebooks: () => import('../views/panels/NotebookPanel'),
    aofCollections: () => import('../views/panels/AOFCollections'),
    editorWrapper: () => import('../views/editor/EditorWrapper'),
    openNotesPanel: () => import('../views/editor/OpenNotesPanel'),
  };

  const importFn = componentMap[componentName];
  if (importFn) {
    prefetchComponent(importFn, componentName);
  }
};

/**
 * Preload critical components immediately on app load
 */
export const preloadCriticalComponents = () => {
  // Preload the most commonly used panels and editor components
  if (typeof window !== 'undefined' && window.requestIdleCallback) {
    window.requestIdleCallback(() => {
      prefetchPanelComponent('notes');
      prefetchPanelComponent('editorWrapper');
      // Preload editor components for instant note editing
      prefetchComponent(() => import('../views/editor'), 'Editor');
      prefetchComponent(() => import('../views/components/RenderNoteEditor'), 'RenderNoteEditor');
      prefetchComponent(() => import('../views/components/NoteEditor'), 'NoteEditor');
    }, { timeout: 2000 });
  } else {
    // Fallback for browsers without requestIdleCallback
    setTimeout(() => {
      prefetchPanelComponent('notes');
      prefetchPanelComponent('editorWrapper');
      // Preload editor components for instant note editing
      prefetchComponent(() => import('../views/editor'), 'Editor');
      prefetchComponent(() => import('../views/components/RenderNoteEditor'), 'RenderNoteEditor');
      prefetchComponent(() => import('../views/components/NoteEditor'), 'NoteEditor');
    }, 100);
  }
};

export default {
  prefetchComponent,
  prefetchNoteData,
  prefetchPanelComponent,
  preloadCriticalComponents,
};

